/**
 * 牙位图 —— FDI 编号，活动义齿业务专用。
 *
 * 关键设计决策（依据 design/02 与 design/10 参考图）：
 *   · 画成牙齿轮廓剪影，不是方块 —— 老板一眼认出这是牙位而非数字格子
 *   · 上颌牙冠朝上、下颌镜像，形状差异天然区分象限
 *   · 状态用【颜色 + 图案 + 图标】三重编码，不能只靠颜色（NFR-3.7）
 *   · 点击循环切换：无 → 缺失 → 卡环基牙 → 本次修复 → 无
 *   · 方向按医生视角：图上左侧是顾客的右侧，必须有文字标注避免歧义
 */

import { useMemo } from 'react'
import { cx } from './ui'
import type { ToothMap } from '../lib/api'

export const UPPER_RIGHT = [18, 17, 16, 15, 14, 13, 12, 11]
export const UPPER_LEFT = [21, 22, 23, 24, 25, 26, 27, 28]
export const LOWER_RIGHT = [48, 47, 46, 45, 44, 43, 42, 41]
export const LOWER_LEFT = [31, 32, 33, 34, 35, 36, 37, 38]

export type ToothState = 'none' | 'missing' | 'clasp' | 'replace'

const CYCLE: ToothState[] = ['none', 'missing', 'clasp', 'replace']

export const STATE_LABEL: Record<ToothState, string> = {
  none: '正常',
  missing: '缺失',
  clasp: '卡环基牙',
  replace: '本次修复',
}

/** 牙齿轮廓：冠部圆润、根部分叉。上颌用这个 path，下颌整体翻转。 */
const TOOTH_PATH =
  'M8 3.5 C4.4 3.5 2.5 6 2.5 9.4 C2.5 12 3.3 13.6 3.9 15.6 L4.6 19.4 C4.8 20.6 5.4 21.3 6.2 21.3 C7 21.3 7.4 20.7 7.6 19.5 L8 16.9 L8.4 19.5 C8.6 20.7 9 21.3 9.8 21.3 C10.6 21.3 11.2 20.6 11.4 19.4 L12.1 15.6 C12.7 13.6 13.5 12 13.5 9.4 C13.5 6 11.6 3.5 8 3.5 Z'

function stateOf(map: ToothMap, n: number): ToothState {
  if (map.replace?.includes(n)) return 'replace'
  if (map.clasp?.includes(n)) return 'clasp'
  if (map.missing?.includes(n)) return 'missing'
  return 'none'
}

function withState(map: ToothMap, n: number, next: ToothState): ToothMap {
  const strip = (arr: number[] | undefined) => (arr ?? []).filter((x) => x !== n)
  const out: ToothMap = {
    missing: strip(map.missing),
    clasp: strip(map.clasp),
    replace: strip(map.replace),
    notes: map.notes,
  }
  if (next === 'missing') out.missing = [...out.missing, n].sort((a, b) => a - b)
  if (next === 'clasp') out.clasp = [...out.clasp, n].sort((a, b) => a - b)
  if (next === 'replace') out.replace = [...out.replace, n].sort((a, b) => a - b)
  return out
}

function Tooth({
  n,
  state,
  lower,
  readOnly,
  compact,
  hideNumber,
  onClick,
}: {
  n: number
  state: ToothState
  lower: boolean
  readOnly?: boolean
  compact?: boolean
  hideNumber?: boolean
  onClick?: () => void
}) {
  const size = compact ? 40 : 44

  const fill = {
    none: 'var(--color-surface)',
    missing: 'var(--color-sunken)',
    clasp: 'var(--color-info-bg)',
    replace: 'var(--color-brand-100)',
  }[state]

  const stroke = {
    none: 'var(--color-line-strong)',
    missing: 'var(--color-line-strong)',
    clasp: 'var(--color-info)',
    replace: 'var(--color-brand-600)',
  }[state]

  const hatchId = `hatch-${n}`

  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={onClick}
      aria-label={hideNumber ? STATE_LABEL[state] : `牙位 ${n}，当前：${STATE_LABEL[state]}`}
      aria-pressed={state !== 'none'}
      title={hideNumber ? STATE_LABEL[state] : `${n} · ${STATE_LABEL[state]}`}
      style={{ width: size, height: hideNumber ? size - 6 : size + 14 }}
      className={cx(
        'flex shrink-0 flex-col items-center justify-start rounded-sm transition-colors',
        !readOnly && 'hover:bg-sunken cursor-pointer',
        readOnly && 'cursor-default',
      )}
    >
      <svg width={size - 6} height={size - 6} viewBox="0 0 16 24" aria-hidden>
        <defs>
          <pattern id={hatchId} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="4" stroke="var(--color-line-strong)" strokeWidth="1.4" />
          </pattern>
        </defs>
        <g transform={lower ? 'translate(0,24) scale(1,-1)' : undefined}>
          <path d={TOOTH_PATH} fill={fill} stroke={stroke} strokeWidth={1.4} />
          {state === 'missing' && (
            <path d={TOOTH_PATH} fill={`url(#${hatchId})`} stroke="none" opacity={0.9} />
          )}
        </g>
        {/* 图标叠在牙形中央，方向不随上下颌翻转 */}
        {state === 'missing' && (
          <path
            d="M5.5 9.5 L10.5 14.5 M10.5 9.5 L5.5 14.5"
            stroke="var(--color-ink-2)"
            strokeWidth={1.6}
            strokeLinecap="round"
          />
        )}
        {state === 'clasp' && (
          <path
            d="M5 13.5 Q8 10.5 11 13.5"
            fill="none"
            stroke="var(--color-info)"
            strokeWidth={1.6}
            strokeLinecap="round"
          />
        )}
        {state === 'replace' && <circle cx="8" cy="12" r="2.2" fill="var(--color-brand-600)" />}
      </svg>
      {!hideNumber && (
        <span className="tnum text-[11px] leading-none text-ink-3">{n}</span>
      )}
    </button>
  )
}

export function ToothChart({
  value,
  onChange,
  readOnly,
  compact,
}: {
  value: ToothMap
  onChange?: (next: ToothMap) => void
  readOnly?: boolean
  compact?: boolean
}) {
  const summary = useMemo(() => {
    const lines: Array<{ label: string; teeth: number[] }> = []
    if (value.missing?.length) lines.push({ label: '缺失', teeth: value.missing })
    if (value.clasp?.length) lines.push({ label: '卡环', teeth: value.clasp })
    if (value.replace?.length) lines.push({ label: '修复', teeth: value.replace })
    return lines
  }, [value])

  const toggle = (n: number) => {
    if (readOnly || !onChange) return
    const cur = stateOf(value, n)
    const next = CYCLE[(CYCLE.indexOf(cur) + 1) % CYCLE.length]!
    onChange(withState(value, n, next))
  }

  const row = (right: number[], left: number[], lower: boolean) => (
    <div className="flex items-start justify-center">
      <div className="flex">
        {right.map((n) => (
          <Tooth
            key={n}
            n={n}
            state={stateOf(value, n)}
            lower={lower}
            readOnly={readOnly}
            compact={compact}
            onClick={() => toggle(n)}
          />
        ))}
      </div>
      {/* 中线：区分左右象限 */}
      <div className="mx-1.5 w-0.5 shrink-0 self-stretch bg-line-strong" />
      <div className="flex">
        {left.map((n) => (
          <Tooth
            key={n}
            n={n}
            state={stateOf(value, n)}
            lower={lower}
            readOnly={readOnly}
            compact={compact}
            onClick={() => toggle(n)}
          />
        ))}
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-3">
      {/* 小屏横向滚动，不压缩牙位到点不中 */}
      <div className="-mx-2 overflow-x-auto px-2 pb-1">
        <div className="mx-auto w-max">
          <div className="flex justify-between px-1 text-[12px] text-ink-3">
            <span>右上</span>
            <span>左上</span>
          </div>
          {row(UPPER_RIGHT, UPPER_LEFT, false)}
          {/* 上下颌之间用间距分隔，不画横线 —— 参考图证明这样更干净 */}
          <div className="h-5" />
          {row(LOWER_RIGHT, LOWER_LEFT, true)}
          <div className="flex justify-between px-1 text-[12px] text-ink-3">
            <span>右下</span>
            <span>左下</span>
          </div>
        </div>
      </div>

      {/* 文字摘要：老板会照着念给顾客听 */}
      {summary.length > 0 && (
        <div className="flex flex-col gap-1 text-[15px]">
          {summary.map((s) => (
            <div key={s.label} className="flex gap-2">
              <span className="text-ink-3">
                {s.label} {s.teeth.length}：
              </span>
              <span className="tnum font-medium">{s.teeth.join(' ')}</span>
            </div>
          ))}
        </div>
      )}

      <Legend />
      {!readOnly && (
        <p className="text-[14px] text-ink-3">
          点一下切换：正常 → 缺失 → 卡环基牙 → 本次修复
        </p>
      )}
    </div>
  )
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line pt-3">
      {(['none', 'missing', 'clasp', 'replace'] as ToothState[]).map((s) => (
        <span key={s} className="flex items-center gap-1.5 text-[14px] text-ink-2">
          <span className="inline-flex">
            <Tooth n={0} state={s} lower={false} readOnly compact hideNumber />
          </span>
          {STATE_LABEL[s]}
        </span>
      ))}
    </div>
  )
}
